import { Transaction, VersionedTransaction } from '@solana/web3.js';
import bs58 from 'bs58';
import {
  generatePhantomKeypair,
  getStoredPhantomKeypair,
  getPhantomSession,
  encryptPhantomPayload,
  getPhantomSharedSecret,
  parsePhantomSignResponse,
  processPhantomSignAllTransactionsResponse,
  clearPhantomSession as clearPhantomEncryptionSession,
} from './phantomMobileEncryption';

export type WalletAdapterName = 'Phantom' | 'Solflare' | 'Backpack' | 'MagicEden' | 'MWA';

/**
 * Detects if the user is on a mobile device
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

  return mobileRegex.test(userAgent);
};

/**
 * Detects if the user is on iOS
 */
export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent || navigator.vendor;
  return /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
};

/**
 * Detects if the user is on Android
 */
export const isAndroid = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent || navigator.vendor;
  return /android/i.test(userAgent);
};

/**
 * Checks if a specific wallet provider is available (wallet extension installed)
 */
export const isWalletAvailable = (walletName: WalletAdapterName): boolean => {
  if (typeof window === 'undefined') return false;

  switch (walletName) {
    case 'Phantom':
      return !!window.phantom?.solana;
    case 'Solflare':
      return !!window.solflare;
    case 'Backpack':
      return !!window.backpack?.solana;
    case 'MagicEden':
      return !!window.magicEden?.solana?.isMagicEden;
    case 'MWA':
      // MWA is only available on mobile devices
      // Check if on mobile AND if MWA wallets are registered via Wallet Standard
      const isMobile = isMobileDevice();
      if (!isMobile) return false;

      // Check if any wallets are available via Wallet Standard
      try {
        // Import here to avoid SSR issues
        const { getWallets } = require('@wallet-standard/app');
        const wallets = getWallets().get();
        return wallets.length > 0;
      } catch {
        return false;
      }
    default:
      return false;
  }
};

/**
 * Detects if the app is opened in Solflare in-app browser
 * Checks if user is on mobile AND Solflare provider is available
 */
export const isInSolflareInAppBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;
  return isMobileDevice() && !!window.solflare;
};

/**
 * Detects if the app is opened in Backpack in-app browser
 * Checks if user is on mobile AND Backpack provider is available
 */
export const isInBackpackInAppBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;
  return isMobileDevice() && !!window.backpack?.solana;
};

/**
 * Detects if the app is opened in Magic Eden in-app browser
 * Checks if user is on mobile AND Magic Eden provider is available
 */
export const isInMagicEdenInAppBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;
  return isMobileDevice() && !!window.magicEden?.solana?.isMagicEden;
};

/**
 * Generates a Phantom deeplink for connecting wallet
 * Following MagicEden pattern: opens app, user approves, returns to website
 *
 * Implements proper encryption flow:
 * 1. Generate dapp keypair
 * 2. Include public key in deeplink
 * 3. Phantom will return encrypted response with session
 */
export const generatePhantomConnectDeeplink = (): string => {
  const currentUrl = window.location.href;
  const dappUrl = encodeURIComponent(currentUrl);
  const redirectUrl = encodeURIComponent(currentUrl);
  const cluster = import.meta.env.VITE_NODE_ENV === 'production' ? 'mainnet-beta' : 'devnet';

  // Generate encryption keypair and store for later use
  const dappKeypair = generatePhantomKeypair();
  const dappPublicKey = bs58.encode(dappKeypair.publicKey);

  // Phantom Universal Link format with encryption
  // This will open Phantom app, request connection, and return to the website with encrypted response
  return `https://phantom.app/ul/v1/connect?dapp_encryption_public_key=${dappPublicKey}&cluster=${cluster}&app_url=${dappUrl}&redirect_link=${redirectUrl}`;
};

/**
 * Generates a Phantom deeplink for signing a transaction
 * Transaction is serialized and encrypted with session
 * User signs in Phantom app and returns to website with encrypted signed transaction
 *
 * Requires prior connection (session must exist)
 */
export const generatePhantomSignTransactionDeeplink = (
  transaction: Transaction | VersionedTransaction,
  redirectUrl?: string
): string => {
  const currentUrl = redirectUrl || window.location.href;
  const dappUrl = encodeURIComponent(currentUrl);

  // Get stored session and shared secret
  const session = getPhantomSession();
  const sharedSecret = getPhantomSharedSecret();

  if (!session || !sharedSecret) {
    throw new Error('Phantom session not found. Please reconnect your wallet.');
  }

  // Serialize transaction to base58 (handle both Transaction and VersionedTransaction)
  let serializedTx: Uint8Array;

  if (transaction instanceof VersionedTransaction) {
    // VersionedTransaction has serialize() method without options
    serializedTx = transaction.serialize();
  } else {
    // Legacy Transaction has serialize() method with options
    serializedTx = transaction.serialize({
      requireAllSignatures: false,
      verifySignatures: false,
    });
  }

  const txBase58 = bs58.encode(serializedTx);

  // Create payload with session and transaction
  const payload = {
    session,
    transaction: txBase58,
  };

  // Encrypt payload
  const { nonce, encryptedPayload } = encryptPhantomPayload(payload, sharedSecret);

  // Get dapp public key from stored keypair (must use same keypair from connect)
  const dappKeypair = getStoredPhantomKeypair();
  if (!dappKeypair) {
    throw new Error('Phantom keypair not found. Please reconnect your wallet.');
  }
  const dappPublicKey = bs58.encode(dappKeypair.publicKey);

  // Phantom Universal Link for signing with encrypted payload
  return `https://phantom.app/ul/v1/signTransaction?dapp_encryption_public_key=${dappPublicKey}&nonce=${nonce}&redirect_link=${dappUrl}&payload=${encryptedPayload}`;
};

/**
 * Generates a Phantom deeplink for signing multiple transactions (batch)
 * Used for VIP batch minting
 *
 * Requires prior connection (session must exist)
 */
export const generatePhantomSignAllTransactionsDeeplink = (
  transactions: (Transaction | VersionedTransaction)[],
  redirectUrl?: string
): string => {
  const currentUrl = redirectUrl || window.location.href;
  const dappUrl = encodeURIComponent(currentUrl);

  // Get stored session and shared secret
  const session = getPhantomSession();
  const sharedSecret = getPhantomSharedSecret();

  if (!session || !sharedSecret) {
    throw new Error('Phantom session not found. Please reconnect your wallet.');
  }

  // Serialize all transactions to base58 (handle both Transaction and VersionedTransaction)
  const serializedTxs = transactions.map(tx => {
    let serialized: Uint8Array;

    if (tx instanceof VersionedTransaction) {
      // VersionedTransaction has serialize() method without options
      serialized = tx.serialize();
    } else {
      // Legacy Transaction has serialize() method with options
      serialized = tx.serialize({
        requireAllSignatures: false,
        verifySignatures: false,
      });
    }

    return bs58.encode(serialized);
  });

  // Create payload with session and transactions
  const payload = {
    session,
    transactions: serializedTxs,
  };

  // Encrypt payload
  const { nonce, encryptedPayload } = encryptPhantomPayload(payload, sharedSecret);

  // Get dapp public key from stored keypair (must use same keypair from connect)
  const dappKeypair = getStoredPhantomKeypair();
  if (!dappKeypair) {
    throw new Error('Phantom keypair not found. Please reconnect your wallet.');
  }
  const dappPublicKey = bs58.encode(dappKeypair.publicKey);

  // Phantom Universal Link for batch signing with encrypted payload
  return `https://phantom.app/ul/v1/signAllTransactions?dapp_encryption_public_key=${dappPublicKey}&nonce=${nonce}&redirect_link=${dappUrl}&payload=${encryptedPayload}`;
};

/**
 * Generates a Phantom deeplink for signing a message
 * Used for wallet ownership verification (e.g., setting primary wallet)
 *
 * Requires prior connection (session must exist)
 *
 * @param message - The message bytes to sign
 * @param displayEncoding - How to display the message ('utf8' or 'hex')
 * @param redirectUrl - Optional URL to redirect back to
 */
export const generatePhantomSignMessageDeeplink = (
  message: Uint8Array,
  displayEncoding: 'utf8' | 'hex' = 'utf8',
  redirectUrl?: string
): string => {
  const currentUrl = redirectUrl || window.location.href;
  const dappUrl = encodeURIComponent(currentUrl);

  // Get stored session and shared secret
  const session = getPhantomSession();
  const sharedSecret = getPhantomSharedSecret();

  if (!session || !sharedSecret) {
    throw new Error('Phantom session not found. Please reconnect your wallet.');
  }

  // Convert message to base58 (required by Phantom)
  const messageBase58 = bs58.encode(message);

  // Create payload with session and message
  const payload = {
    session,
    message: messageBase58,
    display: displayEncoding, // How Phantom should display the message to the user
  };

  // Encrypt payload
  const { nonce, encryptedPayload } = encryptPhantomPayload(payload, sharedSecret);

  // Get dapp public key from stored keypair (must use same keypair from connect)
  const dappKeypair = getStoredPhantomKeypair();
  if (!dappKeypair) {
    throw new Error('Phantom keypair not found. Please reconnect your wallet.');
  }
  const dappPublicKey = bs58.encode(dappKeypair.publicKey);

  // Phantom Universal Link for message signing with encrypted payload
  return `https://phantom.app/ul/v1/signMessage?dapp_encryption_public_key=${dappPublicKey}&nonce=${nonce}&redirect_link=${dappUrl}&payload=${encryptedPayload}`;
};

/**
 * Generates URL to open current website in Backpack in-app browser
 * Official docs: https://docs.backpack.app/deeplinks/other-methods/browse
 *
 * Opens the specified URL within Backpack's in-app browser.
 * This does not require a session parameter (works before connection).
 */
export const generateBackpackInAppBrowserUrl = (): string => {
  const currentUrl = window.location.href;

  // Backpack browse format: https://backpack.app/ul/v1/browse/{url}?ref={ref}
  // Same format as Solflare - URL goes in path, ref in query parameter
  const encodedUrl = encodeURIComponent(currentUrl);
  const encodedRef = encodeURIComponent(currentUrl);

  return `https://backpack.app/ul/v1/browse/${encodedUrl}?ref=${encodedRef}`;
};

/**
 * Generates URL to open current website in Solflare in-app browser
 * Official docs: https://docs.solflare.com/solflare/technical/deeplinks/other-methods/browse
 */
export const generateSolflareInAppBrowserUrl = (): string => {
  const currentUrl = window.location.href;

  // Solflare uses universal link format: https://solflare.com/ul/v1/browse/{url}?ref={ref}
  // The target URL goes in the path (double-encoded), and ref parameter is required
  const encodedUrl = encodeURIComponent(currentUrl);
  const encodedRef = encodeURIComponent(currentUrl);

  return `https://solflare.com/ul/v1/browse/${encodedUrl}?ref=${encodedRef}`;
};

/**
 * Generates URL to open current website in Magic Eden in-app browser
 *
 * ⚠️ WARNING: Magic Eden's in-app browser URL format is not publicly documented.
 * This implementation uses a placeholder fallback that redirects to the download page.
 *
 * TODO: Research and update with actual Magic Eden in-app browser URL format.
 * Possible formats to test:
 * - https://wallet.magiceden.io/browse/{encodedUrl}
 * - https://wallet.magiceden.io/ul/v1/browse/{encodedUrl}?ref={ref}
 * - https://magiceden.io/wallet/browse/{encodedUrl}
 *
 * Contact Magic Eden developer support for official documentation.
 */
export const generateMagicEdenInAppBrowserUrl = (): string => {
  const currentUrl = window.location.href;
  const encodedUrl = encodeURIComponent(currentUrl);
  const appName = 'Tweetonium';
  const encodedRef = encodeURIComponent(appName);

  // Placeholder fallback - redirects to download page with return URL
  // This will prompt users to download Magic Eden app if not installed
  // Replace with actual in-app browser URL format once confirmed
  return `https://wallet.magiceden.io/download?redirect=${encodedUrl}`;

  // Uncomment the appropriate format once confirmed:
  // Option 1 (similar to Solflare/Backpack):
  // return `https://wallet.magiceden.io/ul/v1/browse/${encodedUrl}?ref=${encodedRef}`;

  // Option 2 (simpler format):
  // return `https://wallet.magiceden.io/browse/${encodedUrl}`;
};

/**
 * Mobile wallet session storage keys
 */
export const MOBILE_WALLET_STORAGE = {
  PENDING_CONNECTION: 'mobile_wallet_pending_connection',
  PENDING_TRANSACTION: 'mobile_wallet_pending_transaction',
  PENDING_BATCH_MINT: 'mobile_wallet_pending_batch_mint',
  PENDING_SIGN_MESSAGE: 'mobile_wallet_pending_sign_message',
  PENDING_PHANTOM_BATCH_UPGRADE: 'mobile_wallet_pending_phantom_batch_upgrade',
  WALLET_ADAPTER: 'mobile_wallet_adapter',
  RETURN_URL: 'mobile_wallet_return_url',
} as const;

/**
 * Session data interfaces
 */
export interface MobilePendingConnection {
  walletAdapter: WalletAdapterName;
  timestamp: number;
  redirected: boolean;
}

export interface MobilePendingTransaction {
  transactionBase64: string;
  walletAdapter: WalletAdapterName;
  timestamp: number;
  eggId?: string;
  returnUrl: string;
}

export interface MobilePendingBatchMint {
  transactions: string[]; // Array of base64 transactions
  walletAdapter: WalletAdapterName;
  timestamp: number;
  eggIds: string[];
  returnUrl: string;
}

export interface MobilePendingSignMessage {
  message: string; // Original message text
  messageBase64: string; // Base64 encoded message bytes
  walletId: number; // Expected signer wallet ID
  walletAddress: string; // Expected signer wallet
  walletAdapter: WalletAdapterName;
  timestamp: number;
  returnUrl: string;
  purpose?: string; // e.g., 'set_primary_wallet'
}

export interface MobilePendingPhantomBatchUpgrade {
  nftOwnershipId: number;
  nftMetadataUri: string;
  nftImageUrl: string;
  traitTransactions: {
    traitOwnershipId: number;
    metadataUri: string;
    action: 'equip' | 'unequip';
  }[];
  nftId: number;
  nftName: string;
  timestamp: number;
  imageTrackingId?: number;
}

/**
 * Saves pending connection state for mobile flow
 */
export const savePendingConnection = (walletAdapter: WalletAdapterName): void => {
  const data: MobilePendingConnection = {
    walletAdapter,
    timestamp: Date.now(),
    redirected: true,
  };

  localStorage.setItem(MOBILE_WALLET_STORAGE.PENDING_CONNECTION, JSON.stringify(data));

  // Verify it was saved
  const saved = localStorage.getItem(MOBILE_WALLET_STORAGE.PENDING_CONNECTION);

  localStorage.setItem(MOBILE_WALLET_STORAGE.WALLET_ADAPTER, walletAdapter);
};

/**
 * Gets pending connection state
 */
export const getPendingConnection = (): MobilePendingConnection | null => {
  const data = localStorage.getItem(MOBILE_WALLET_STORAGE.PENDING_CONNECTION);

  if (!data) {
    return null;
  }

  try {
    const parsed = JSON.parse(data) as MobilePendingConnection;

    // Check if connection attempt is less than 5 minutes old
    const isExpired = Date.now() - parsed.timestamp > 5 * 60 * 1000;
    if (isExpired) {
      clearPendingConnection();
      return null;
    }

    return parsed;
  } catch (error) {
    return null;
  }
};

/**
 * Clears pending connection state
 */
export const clearPendingConnection = (): void => {
  localStorage.removeItem(MOBILE_WALLET_STORAGE.PENDING_CONNECTION);
};

/**
 * Saves pending transaction for mobile signing flow
 */
export const savePendingTransaction = (
  transaction: Transaction,
  walletAdapter: WalletAdapterName,
  eggId?: string
): void => {
  const serialized = transaction.serialize({
    requireAllSignatures: false,
    verifySignatures: false,
  });
  const txBase64 = Buffer.from(serialized).toString('base64');

  const data: MobilePendingTransaction = {
    transactionBase64: txBase64,
    walletAdapter,
    timestamp: Date.now(),
    eggId,
    returnUrl: window.location.href,
  };

  localStorage.setItem(MOBILE_WALLET_STORAGE.PENDING_TRANSACTION, JSON.stringify(data));
};

/**
 * Gets pending transaction
 */
export const getPendingTransaction = (): MobilePendingTransaction | null => {
  const data = localStorage.getItem(MOBILE_WALLET_STORAGE.PENDING_TRANSACTION);
  if (!data) return null;

  try {
    const parsed = JSON.parse(data) as MobilePendingTransaction;

    // Check if transaction is less than 10 minutes old
    const isExpired = Date.now() - parsed.timestamp > 10 * 60 * 1000;
    if (isExpired) {
      clearPendingTransaction();
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

/**
 * Clears pending transaction
 */
export const clearPendingTransaction = (): void => {
  localStorage.removeItem(MOBILE_WALLET_STORAGE.PENDING_TRANSACTION);
};

/**
 * Saves pending batch mint transactions
 */
export const savePendingBatchMint = (
  transactions: Transaction[],
  walletAdapter: WalletAdapterName,
  eggIds: string[]
): void => {
  const txsBase64 = transactions.map(tx => {
    const serialized = tx.serialize({
      requireAllSignatures: false,
      verifySignatures: false,
    });
    return Buffer.from(serialized).toString('base64');
  });

  const data: MobilePendingBatchMint = {
    transactions: txsBase64,
    walletAdapter,
    timestamp: Date.now(),
    eggIds,
    returnUrl: window.location.href,
  };

  localStorage.setItem(MOBILE_WALLET_STORAGE.PENDING_BATCH_MINT, JSON.stringify(data));
};

/**
 * Gets pending batch mint
 */
export const getPendingBatchMint = (): MobilePendingBatchMint | null => {
  const data = localStorage.getItem(MOBILE_WALLET_STORAGE.PENDING_BATCH_MINT);
  if (!data) return null;

  try {
    const parsed = JSON.parse(data) as MobilePendingBatchMint;

    // Check if batch mint is less than 10 minutes old
    const isExpired = Date.now() - parsed.timestamp > 10 * 60 * 1000;
    if (isExpired) {
      clearPendingBatchMint();
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

/**
 * Clears pending batch mint
 */
export const clearPendingBatchMint = (): void => {
  localStorage.removeItem(MOBILE_WALLET_STORAGE.PENDING_BATCH_MINT);
};

/**
 * Saves pending sign message for mobile signing flow
 */
export const savePendingSignMessage = (
  message: string,
  messageBytes: Uint8Array,
  walletId: number,
  walletAddress: string,
  walletAdapter: WalletAdapterName,
  purpose?: string
): void => {
  const messageBase64 = Buffer.from(messageBytes).toString('base64');

  const data: MobilePendingSignMessage = {
    message,
    messageBase64,
    walletId,
    walletAddress,
    walletAdapter,
    timestamp: Date.now(),
    returnUrl: window.location.href,
    purpose,
  };

  localStorage.setItem(MOBILE_WALLET_STORAGE.PENDING_SIGN_MESSAGE, JSON.stringify(data));
};

/**
 * Gets pending sign message
 */
export const getPendingSignMessage = (): MobilePendingSignMessage | null => {
  const data = localStorage.getItem(MOBILE_WALLET_STORAGE.PENDING_SIGN_MESSAGE);
  if (!data) return null;

  try {
    const parsed = JSON.parse(data) as MobilePendingSignMessage;

    // Check if sign message is less than 10 minutes old
    const isExpired = Date.now() - parsed.timestamp > 10 * 60 * 1000;
    if (isExpired) {
      clearPendingSignMessage();
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

/**
 * Clears pending sign message
 */
export const clearPendingSignMessage = (): void => {
  localStorage.removeItem(MOBILE_WALLET_STORAGE.PENDING_SIGN_MESSAGE);
};

/**
 * Saves pending Phantom batch upgrade data before redirect
 * Used for NFT upgrade flow with multiple transactions (NFT + traits)
 */
export const savePendingPhantomBatchUpgrade = (data: Omit<MobilePendingPhantomBatchUpgrade, 'timestamp'>): void => {
  const upgradeData: MobilePendingPhantomBatchUpgrade = {
    ...data,
    timestamp: Date.now(),
  };
  localStorage.setItem(MOBILE_WALLET_STORAGE.PENDING_PHANTOM_BATCH_UPGRADE, JSON.stringify(upgradeData));
};

/**
 * Gets pending Phantom batch upgrade data
 */
export const loadPendingPhantomBatchUpgrade = (): MobilePendingPhantomBatchUpgrade | null => {
  const data = localStorage.getItem(MOBILE_WALLET_STORAGE.PENDING_PHANTOM_BATCH_UPGRADE);
  if (!data) return null;

  try {
    const parsed = JSON.parse(data) as MobilePendingPhantomBatchUpgrade;

    // Check if upgrade data is less than 10 minutes old
    const isExpired = Date.now() - parsed.timestamp > 10 * 60 * 1000;
    if (isExpired) {
      clearPendingPhantomBatchUpgrade();
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

/**
 * Clears pending Phantom batch upgrade data
 */
export const clearPendingPhantomBatchUpgrade = (): void => {
  localStorage.removeItem(MOBILE_WALLET_STORAGE.PENDING_PHANTOM_BATCH_UPGRADE);
};

/**
 * Checks if a valid Phantom session exists
 * Required for signMessage and signTransaction operations
 */
export const hasValidPhantomSession = (): boolean => {
  const session = getPhantomSession();
  const sharedSecret = getPhantomSharedSecret();
  return !!(session && sharedSecret);
};

/**
 * Parse and process Phantom signAllTransactions response from URL parameters
 * Combines URL parsing and decryption into a single function
 * Returns signed transactions as Uint8Array buffers ready to send to Solana
 */
export const parsePhantomSignAllTransactionsResponse = (
  urlParams: URLSearchParams
): { signedTransactions: Uint8Array[] } | null => {
  // Parse response from URL
  const signResponse = parsePhantomSignResponse();
  if (!signResponse) return null;

  // Process and decrypt the response
  const { transactions } = processPhantomSignAllTransactionsResponse(signResponse);

  // Convert base58 transactions to Uint8Array buffers
  const signedTransactions = transactions.map(txBase58 => bs58.decode(txBase58));

  return { signedTransactions };
};

/**
 * Re-export clearPhantomSession for convenience
 * Clears Phantom encryption session (keypair and shared secret)
 */
export const clearPhantomSession = clearPhantomEncryptionSession;

/**
 * Clears all mobile wallet session data
 */
export const clearAllMobileWalletData = (): void => {
  clearPendingConnection();
  clearPendingTransaction();
  clearPendingBatchMint();
  clearPendingSignMessage();
  clearPendingPhantomBatchUpgrade();
  localStorage.removeItem(MOBILE_WALLET_STORAGE.WALLET_ADAPTER);
  localStorage.removeItem(MOBILE_WALLET_STORAGE.RETURN_URL);
};

/**
 * Handles deeplink redirect for mobile wallet
 */
export const redirectToMobileWallet = (deeplink: string): void => {
  // Save current URL for return
  localStorage.setItem(MOBILE_WALLET_STORAGE.RETURN_URL, window.location.href);


  // Small delay to ensure localStorage.setItem completes before navigation
  // Browser may not flush localStorage if we redirect immediately
  setTimeout(() => {
    window.location.href = deeplink;
  }, 100); // 100ms delay
};
